import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErrorOwner, ErrorRelationState, QueueLogClient, QueueLogMessageDto } from '../../api/api';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { useCockpit } from '../data/CockpitContext';
import { clock, dayKey, dayLabel } from '../data/format';
import { usePoll } from '../data/usePoll';
import { Card, Empty, ErrorNotice, Loading } from '../components/ui';
import { objectName } from './issueModel';
import { v2 } from '../paths';

type Status = 'you' | 'us' | 'auto' | 'done';

const STATUS: Record<Status, { label: string; cls: string }> = {
    you: { label: 'Actie voor jou', cls: 'own cu' },
    us: { label: 'Wij lossen op', cls: 'own us' },
    auto: { label: 'Gaat vanzelf', cls: 'own au' },
    done: { label: 'Opgelost', cls: 'chip ok' },
};

const statusOf = (m: QueueLogMessageDto): Status => {
    if (m.exactState === ErrorRelationState.InExact || m.exactState === ErrorRelationState.Skipped) return 'done';
    switch (m.interpretation?.owner) {
        case ErrorOwner.Customer:
            return 'you';
        case ErrorOwner.Automatic:
            return 'auto';
        default:
            return 'us';
    }
};

const PERIODS = [1, 7, 30];

/**
 * What went wrong between Chargebee and Exact, in words a customer reads: per day, with who picks it up.
 * The one screen a customer account sees; support sees the same page to know what the customer sees.
 */
export const ErrorLogPage = () => {
    const { customer = '', environment = '' } = useParams();
    const axios = useAxiosSwagger();
    const { isAdmin } = useCockpit();
    const [days, setDays] = useState(7);
    const [filter, setFilter] = useState<Status | 'all'>('all');
    const [open, setOpen] = useState<string>();

    const log = usePoll<QueueLogMessageDto[]>(
        axios
            ? async () => {
                  const to = new Date();
                  const from = new Date(to.getTime() - days * 86400000);
                  return (await new QueueLogClient(undefined, axios).range(customer, environment, from, to)) ?? [];
              }
            : null,
        30000,
        [axios, customer, environment, days],
        `errorlog/${customer}/${environment}/${days}`
    );

    // One line per object: the newest failure says where it stands.
    const entries = useMemo(() => {
        const newest = new Map<string, QueueLogMessageDto>();
        (log.data ?? []).forEach((m) => {
            const key = `${m.objectType}|${m.relationId}`;
            const seen = newest.get(key);
            if (!seen || seen.timestamp < m.timestamp) newest.set(key, m);
        });
        return Array.from(newest.values()).sort((a, b) => b.timestamp.localeCompare(a.timestamp));
    }, [log.data]);

    const counts = entries.reduce<Record<Status, number>>((n, m) => ({ ...n, [statusOf(m)]: n[statusOf(m)] + 1 }), { you: 0, us: 0, auto: 0, done: 0 });
    const shown = entries.filter((m) => filter === 'all' || statusOf(m) === filter);
    const asks = entries.filter((m) => statusOf(m) === 'you');
    const byDay = shown.reduce<{ day: string; label: string; items: QueueLogMessageDto[] }[]>((acc, m) => {
        const key = dayKey(m.timestamp);
        const group = acc.find((g) => g.day === key);
        if (group) group.items.push(m);
        else acc.push({ day: key, label: dayLabel(m.timestamp), items: [m] });
        return acc;
    }, []);


    return (
        <div style={{ maxWidth: 1040, width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="toolbar">
                <span className="h2">Foutenlogboek</span>
                <div className="seg" role="group" aria-label="Periode">
                    {PERIODS.map((d) => (
                        <button key={d} className={days === d ? 'on' : ''} onClick={() => setDays(d)}>
                            {d === 1 ? 'Vandaag' : `${d} dagen`}
                        </button>
                    ))}
                </div>
            </div>

            {isAdmin && <div className="mut" style={{ fontSize: 13 }}>Zo ziet de klant dit scherm.</div>}

            <div className="tiles">
                {(['you', 'us', 'auto', 'done'] as Status[]).map((s) => (
                    <button key={s} className={`card tile ${s === 'you' && counts.you ? 'cu' : ''}`} style={filter === s ? { borderColor: '#5CC8FF' } : undefined} onClick={() => setFilter(filter === s ? 'all' : s)}>
                        <span className="l">{STATUS[s].label}</span>
                        <span className={`v ${s === 'you' && counts.you ? 'warn' : s === 'done' ? 'ok' : ''}`}>{log.data ? counts[s] : '…'}</span>
                    </button>
                ))}
            </div>

            {asks.length > 0 && filter !== 'done' && (
                <section className="next cu">
                    <span className="who cu">JIJ</span>
                    <div className="stack" style={{ gap: 10 }}>
                        <div style={{ fontSize: 18, fontWeight: 600 }}>
                            {asks.length === 1 ? 'Er is één ding dat alleen jij kunt oplossen' : `Er zijn ${asks.length} dingen die alleen jij kunt oplossen`}
                        </div>
                        {asks.slice(0, 3).map((m) => (
                            <div key={m.rowKey}>
                                <b>{m.interpretation?.title ?? 'Actie nodig'}</b>
                                <div className="mut" style={{ fontSize: 13 }}>{objectName(m.objectType)} {m.additionalInfo2 || m.relationId}</div>
                                {(m.interpretation?.steps?.length ?? 0) > 0 && (
                                    <ol className="steps-num" style={{ marginTop: 8 }}>
                                        {m.interpretation!.steps!.slice(0, 4).map((s, i) => (
                                            <li key={i}><span>{s}</span></li>
                                        ))}
                                    </ol>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <ErrorNotice error={log.error} />
            {log.pending && <Loading>Foutenlogboek van {days === 1 ? 'vandaag' : `${days} dagen`} ophalen…</Loading>}
            {log.data && byDay.length === 0 && <Empty>Niets misgegaan in deze periode.</Empty>}

            {byDay.map((g) => (
                <Card key={g.day} title={g.label} right={<span className="mut" style={{ fontSize: 12 }}>{g.items.length}</span>}>
                    {g.items.map((m) => {
                        const s = statusOf(m);
                        const key = m.rowKey ?? `${m.objectType}|${m.relationId}`;
                        return (
                            <React.Fragment key={key}>
                                <button className="li" style={{ gridTemplateColumns: '54px minmax(0,1fr) auto' }} onClick={() => setOpen(open === key ? undefined : key)}>
                                    <span className="mono mut">{clock(m.timestamp)}</span>
                                    <span>
                                        <span className="t" style={{ fontWeight: 500 }}>{m.interpretation?.title ?? m.errorText ?? 'Onbekende fout'}</span>
                                        <span className="m">
                                            <span>{objectName(m.objectType)}</span>
                                            <span className="mono">{m.additionalInfo2 || m.relationId}</span>
                                        </span>
                                    </span>
                                    <span className={STATUS[s].cls}>{STATUS[s].label}</span>
                                </button>
                                {open === key && (
                                    <div style={{ padding: '4px 16px 16px 82px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <div className="stepper">
                                            <span className="sp"><span className="ic ok">✓</span>Chargebee</span>
                                            <span className="sl ok" />
                                            <span className="sp"><span className={`ic ${s === 'done' ? 'ok' : 'bad'}`}>{s === 'done' ? '✓' : '✕'}</span>Koppeling</span>
                                            <span className={s === 'done' ? 'sl ok' : 'sl'} />
                                            <span className="sp"><span className={`ic ${s === 'done' ? 'ok' : 'todo'}`}>{s === 'done' ? '✓' : ''}</span>Exact</span>
                                        </div>
                                        {m.interpretation?.explanation && <div className="mut">{m.interpretation.explanation}</div>}
                                        {isAdmin && m.relationId && (
                                            <div>
                                                <Link className="btn sm" to={v2.object(customer, environment, m.additionalInfo2 || m.relationId)}>
                                                    Dossier en timeline
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Card>
            ))}
        </div>
    );
};
